import React, {
  useState,
  useMemo,
  useEffect,
  Fragment,
  useContext,
  useCallback,
} from "react";
import {
  Fab,
  Box,
  Tab,
  Tabs,
  CircularProgress,
  Typography,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";

import AssigneePicker from "../assignee/AssigneePicker";
import { backdropProp } from "../../styles/styles";
import { StaticWindow } from "../../components/ui/windows";
import { ActionDial } from "../../components/ui/buttons";
import { Notification } from "../../components/ui/Notification";
import ConfirmationDialog from "../../components/boilerplate/ConfirmationDialog";
import * as api from "../../services";
import { loadTicket } from "./ticket";

import { EventContext } from "../../context/EventContext";
import { DefinitionsContext } from "../../context/DefinitionsContext";
import { eventUserToAssignee } from "../assignee/AssigneeObject";
import TicketSales from "./TicketSales";
import { IncidentReport } from "./IncidentReport";
import { TabPanel } from "../../components/ui/containers";
import { CurrentUserContext } from "../../context/CurrentUserContext";
import { hasPermission } from "../../components/wrapper/Permission";
import { eventConflictMessage } from "../../utils/eventConflict";
import { useAreInputsValid } from "../../components/inputs";
import { useHideActions } from "../../hooks/useHideActions";
import { OpenEvaluationDialog } from "./OpenEvaluationDialog";

function EventEvalEditor({
  isOpen,
  eventId = null,
  onClose,
  onUpdate,
  children,
}) {
  const actionList = useMemo(
    () => [
      { name: "Info", permission: 3, onlyValid: false },
      { name: "Edit", permission: 5, onlyValid: false },
      { name: "Save", permission: 5, onlyValid: true },
      { name: "Submit", permission: 9, onlyValid: true },
    ],
    []
  );
  const { currentUser } = useContext(CurrentUserContext);
  const { translationList } = useContext(DefinitionsContext);
  const [isValid, onValidChange] = useAreInputsValid();

  // Data
  const [event, setEvent] = useState(null);
  const [eventUsers, setEventUsers] = useState(null);
  const [notify, setNotify] = useState({ text: "", severity: "info" });
  const [assignees, setAssignees] = useState(null); //local state of eventUsers
  const [tickets, setTickets] = useState([]);
  const [report, setReport] = useState("");

  // Dialogs
  const [showNotify, setShowNotify] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showUnsaved, setShowUnsaved] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // State
  const [isDisabled, setIsDisabled] = useState(true);
  const [isFinal, setIsFinal] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [hasPlaceholder, setHasPlaceholder] = useState(false);
  const [updateCounter, setUpdateCounter] = useState(0);
  const [renderUpdateCounter, setRenderUpdateCounter] = useState(0); //This one only renders, doesn't fetch
  const [hideActions, setIsValid, setPermission] = useHideActions(actionList);

  /* --Functions------------------------------------------------------------- */
  /* ------------------------------------------------------------------------ */
  function markConflictingAssignees(confictingAssignees) {
    if (!confictingAssignees) {
      return;
    }
    const conflictIds = confictingAssignees.map((el) => el.user?.id);
    const newAssignees = [];
    for (const el of assignees) {
      if (conflictIds.includes(el.user?.id)) {
        el.setError(true);
      }
      newAssignees.push(el);
    }
    setAssignees([...newAssignees]);
  }

  useEffect(() => {console.log('Update Counter', updateCounter)}, [updateCounter]);

  // Checks if user should be warned about unsaved changes
  function handleClose() {
    const isMutated = !assignees
      .map((el) => el.status)
      .every((val) => val === "original");
    const isDeletion = findDeletions().length !== 0;
    if ((isMutated || isDeletion) && !isFinal) {
      setShowUnsaved(true);
    } else {
      close();
    }
  }

  function showNotification(shouldShow) {
    if (event?.isEffective === true) {
      setShowNotify(shouldShow);
    }
  }

  // Resets states and closes dialog
  function close() {
    resetState();
    onClose();
  }

  // Reset all states
  function resetState() {
    setEvent(null);
    setEventUsers(null);
    setAssignees(null);
    setNotify({ text: "", severity: "info" });

    setShowNotify(false);
    setShowDelete(false);
    setShowUnsaved(false);

    setIsDisabled(true);
    setIsLocked(false);
    setReport("");
  }

  // Check if any delete eventUser calls are needed when saving an edited event
  function findDeletions() {
    const assigneeIds = assignees
      .map((el) => el.eventUserId)
      .filter((el) => Number.isInteger(el));
    const eventUserIds = eventUsers.map((el) => el.eventUserId);

    return eventUserIds.filter((el) => !assigneeIds.includes(el));
  }

  async function handleEvaluationOpen() {
    if (!eventId) {
      console.warn("No eventId");
      return;
    }
    const res = await api.openEvaluation(eventId);
    if (res.status === 200) {
      setNotify({
        text: translationList.evaluationOpened,
        severity: "success",
      });
      setShowNotify(true);
      setIsFinal(true);
      setUpdateCounter(updateCounter + 1);
    } else {
      setNotify({ text: translationList.somethingWrong, severity: "error" });
      showNotification(true);
    }
  }

  async function handleSubmit() {
    //Generating Timerequests
    const success = await saveEvent();
    if (!success) { return; }
    const res = await api.generateAllTimeRequests(event.id);
    if (res.status === 200) {
      setNotify({
        text: translationList.eventEvalSubmitted,
        severity: "success",
      });
      showNotification(true);
      setIsDisabled(true);
      setIsFinal(true);
      setShowSubmit(false);
      onUpdate();
    } else {
      setNotify({ text: translationList.somethingWrong, severity: "error" });
      showNotification(true);
    }
  }

  async function handleSave() {
    await saveEvent();
  }

  async function saveEvent() {
    if (assignees?.length === 0 || !assignees) {
      setNotify({
        text: translationList.saveWithoutAssigningSomeone,
        severity: "warning",
      });
      showNotification(true);
      return;
    }
    setIsLocked(true);
    const responses = [];

    //const reqEventType = (eventType !== '') ? eventType : null;
    //responses.push( await api.updateEvent(event.id, title, date, startTime, endTime, reqEventType, (res) => {
    //  setEvent(res.data)}) );

    //Update and Create Assignees
    let body = [];
    for (let el of assignees) {
      const req = el.getRequest();
      if (req) {
        body.push(req);
      }
    }

    //Saving effective event users
    if (body.length > 0) {
      try {
        responses.push(await api.addAssignees(event.id, body, true));
      } catch (err) {
        if (err.response?.status === 409) {
          markConflictingAssignees(err.response?.data);
          setIsLocked(false);
          setNotify({
            text: eventConflictMessage(err.response?.data, translationList),
            severity: "error",
          });
          showNotification(true);
          console.log('Returning early')
          return false;
        }
      }
    }
    console.log('Passed conflict check')

    //Saving ticket sales
    for (let el of tickets) {
      if (el.status === "new") {
        api.addTicket(el.json);
      } else if (el.status === "modified") {
        api.updateTicket(el.id, el.json);
      }
    }

    //Saving report
    if (report !== "") {
      try {
        //TODO: Fix
        responses.push(await api.postReport(event.id, report));
      } catch (err) {
        console.error(err);
        setNotify({ text: "Unable to save report", severity: "error" });
        showNotification(true);
      }
    }

    if (responses.every((el) => el.status === 200)) {
      setNotify({
        text: translationList.savedChangesSuccessfully,
        severity: "success",
      });
    } else {
      setNotify({ text: translationList.somethingWrong, severity: "error" });
    }
    showNotification(true);

    //api.getEvent(event.id, onLoadEvent);
    setIsLocked(false);
    onUpdate();

    setUpdateCounter(updateCounter + 1);
    return true;
  }

  const isResponsibleFor = useCallback(
    (userId, responsibility) => {
      const users = eventUsers.filter(
        (el) => el.responsibilityTypeName === responsibility
      );
      if (users.length === 0) {
        return false;
      }
      return users.filter((el) => el.userId === userId).length > 0;
    },
    [eventUsers]
  );

  const checkIsPermitted = useCallback(
    (userId) => {
      const isRegisterResponsible = isResponsibleFor(userId, "register");
      const isEveningResponsible = isResponsibleFor(userId, "evening");
      const isBoss = hasPermission(5, currentUser);
      return isRegisterResponsible || isEveningResponsible || isBoss;
    },
    [currentUser, isResponsibleFor]
  );

  /* --Effects--------------------------------------------------------------- */
  /* ------------------------------------------------------------------------ */
  useEffect(() => {
    if (!assignees) {
      setIsValid(false);
      return;
    }
    setHasPlaceholder(assignees.some((el) => !el?.user));
  }, [assignees]);

  useEffect(() => {
    if (report === "") {
      onValidChange("ReportFilled", false);
      return;
    }
    onValidChange("ReportFilled", true);
  }, [report]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (!isValid) {
      if (report === "") {
        setNotify({
          text: translationList.reportMustNotEmpty,
          severity: "warning",
        });
      } else {
        setNotify({
          text: translationList.invalidEntriesTicketTab,
          severity: "warning",
        });
      }
      showNotification(true);
    } else if (hasPlaceholder) {
      setNotify({
        text: translationList.submitAssigneesPlaceholder,
        severity: "warning",
      });
      showNotification(true);
    }
    setIsValid(!hasPlaceholder && isValid);
  }, [isOpen, hasPlaceholder, setIsValid, isValid, translationList]);

  useEffect(() => {
    if (!eventUsers || !currentUser) {
      setPermission(0);
      return;
    }
    if (hasPermission(9, currentUser)) {
      setPermission(9);
      return;
    }
    if (checkIsPermitted(currentUser.id)) {
      setPermission(5);
      return;
    }
    setPermission(0);
  }, [currentUser, eventUsers, checkIsPermitted]);

  // Load default values from backend into inputs
  useEffect(() => {
    if (event) {
      setEvent(event);
    }
  }, [event]);

  // Makes it so dialog is empty when opening after closing another one
  useEffect(() => {
    if (!isOpen) {
      setEvent();
      setEventUsers([]);
    }
  }, [isOpen]);

  /* --Server Communication-------------------------------------------------- */
  /* ------------------------------------------------------------------------ */
  // Load Event from event id
  useEffect(() => {
    if (eventId && isOpen) {
      (async () => {
        const res = await api.getEvent(eventId);
        //const res2 = await api.getReport(eventId);
        setReport(res.event?.comment?.comment || "");
        setEvent(res.event);
        const hasEffective = res.eventUsers
          .map((el) => el.isEffectiveEventUser)
          .includes(true);
        setEventUsers(
          res.eventUsers.filter(
            (el) => el.isEffectiveEventUser === hasEffective
          )
        );
        setIsFinal(res.event.isFinal);
      })();
    }
  }, [eventId, isOpen, updateCounter]);

  // Load tickets from backend
  useEffect(() => {
    async function fetchTicket() {
      let res = await api.getTicket(event.id);
      if (!res.data) {
        return;
      }
      let loadedTickets = res.data.map((el) => loadTicket(el));
      //Double check to avoid double tickets
      if (loadedTickets.length === 0) {
        res = await api.getTicket(event.id);
        loadedTickets = res.data.map((el) => loadTicket(el));
      }
      if (loadedTickets.length === 0) {
        await api.addTicket({
          eventId: event.id,
          ticketTypeId: 1,
          quantity: 0,
          price: 0,
        });
        await api.addTicket({
          eventId: event.id,
          ticketTypeId: 2,
          quantity: 0,
          price: 0,
        });
        res = await api.getTicket(event.id);
        if (!res.data) {
          return;
        }
        loadedTickets = res.data.map((el) => loadTicket(el));
      }
      setTickets(loadedTickets);
    }
    if (event) {
      fetchTicket();
    }
  }, [event]);

  // Load eventUsers from backend as Assignees
  useEffect(() => {
    const newAssignees = [];
    async function fetchUsers() {
      let tmp;
      for await (let el of eventUsers) {
        tmp = eventUserToAssignee(el);
        if (el.userId) {
          tmp.user = (await api.getUser(el.userId)).data;
        }
        newAssignees.push(tmp);
      }
      setAssignees(newAssignees);
    }
    if (eventUsers) {
      fetchUsers();
    }
  }, [eventUsers]);

  /* --Render---------------------------------------------------------------- */
  /* ------------------------------------------------------------------------ */
  if (assignees === null) return <CircularProgress />;

  return (
    <EventContext.Provider
      value={{
        event,
        eventUsers,
        assignees,
        setAssignees,
        //eventState: {
        //  date: date,
        //  startTime: startTime,
        //  endTime: endTime,
        //},
      }}
    >
      <Fragment>
        {/*--Dialogs-----------------------------------------------------------*/}
        <ConfirmationDialog
          isOpen={showDelete}
          title={translationList.areYouSure}
          text={`${translationList.wantDeleteEvent} '${event?.name}'?`}
          onClose={() => setShowDelete(false)}
          onConfirm={() => {
            api.deleteEvent(event.id, onUpdate);
            close();
          }}
        />

        <ConfirmationDialog
          isOpen={showUnsaved}
          text={translationList.unsavedChangesConfirmation}
          onClose={() => setShowUnsaved(false)}
          onConfirm={() => {
            close();
          }}
        />
        <ConfirmationDialog
          isOpen={showSubmit}
          text={translationList.sureToSubmitEventEval}
          onClose={() => setShowSubmit(false)}
          onConfirm={() => handleSubmit()}
        />
        {/*--Form--------------------------------------------------------------*/}
        <StaticWindow
          isOpen={isOpen}
          onClose={handleClose}
          slotProps={backdropProp}
          sx={{ width: "90%" }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              width: "100%",
            }}
          >
            <Notification
              text={notify.text}
              severity={notify.severity}
              isOpen={showNotify}
              onClose={() => setShowNotify(false)}
            />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography variant="h5">{event?.name}</Typography>
              {isFinal && <LockIcon color={"disabled"} />}
            </Box>

            {event?.isEffective === false ? (
              <OpenEvaluationDialog
                isPermitted={checkIsPermitted(currentUser.id)}
                onChange={handleEvaluationOpen}
              />
            ) : (
              <Fragment>
                {checkIsPermitted(currentUser.id) && (
                    <Tabs
                        value={activeTab}
                        onChange={(e, val) => setActiveTab(val)}
                    >
                      <Tab label={translationList.hours} />
                      <Tab label={translationList.tickets} />
                      <Tab
                          label={translationList.report}
                          sx={{
                            color: report.trim() === "" ? "#f44336" : "inherit",
                          }}
                      />
                    </Tabs>
                )}

                <TabPanel value={activeTab} index={0}>
                  <AssigneePicker
                    isDisabled={isDisabled || isLocked}
                    isEval={true}
                  />
                </TabPanel>
                <TabPanel value={activeTab} index={1}>
                  <TicketSales
                    tickets={tickets}
                    disabled={
                      isFinal ||
                      isDisabled ||
                      isLocked ||
                      !checkIsPermitted(currentUser.id)
                    }
                    setTickets={setTickets}
                    onValidChange={onValidChange}
                  />
                </TabPanel>
                <TabPanel value={activeTab} index={2}>
                  <IncidentReport
                    report={report}
                    setReport={setReport}
                    onValidChange={onValidChange}
                    disabled={
                      isFinal ||
                      isDisabled ||
                      isLocked ||
                      !checkIsPermitted(currentUser.id)
                    }
                  />
                </TabPanel>
                {!isFinal && !isLocked && checkIsPermitted(currentUser.id) && (
                  <ActionDial
                    hideActions={hideActions}
                    onAction={{
                      Info: () => setIsDisabled(true),
                      Edit: () => setIsDisabled(false),
                      Save: () => handleSave(),
                      Submit: () => setShowSubmit(true),
                    }}
                  />
                )}
              </Fragment>
            )}
          </Box>
        </StaticWindow>
      </Fragment>
    </EventContext.Provider>
  );
}

export { EventEvalEditor };
export default EventEvalEditor;
